import { rosterProviderName } from '../../services/roster/roster.columns';
import {
    IRosterFacilityValues,
    IRosterProviderValues,
    rosterFacilityDraft,
    rosterFacilitySchema,
    rosterFacilityValues,
    rosterProviderDraft,
    rosterProviderSchema,
    rosterProviderValues,
} from '../../services/roster/roster.form';
import { rosterComingSoon, RosterFacility, RosterProvider, RosterRecord } from '../../services/roster/roster.types';
import { ToastContext } from '../../services/toast/toast';
import { RosterStore } from '../../stores/roster/roster.store';
import { Banner } from '../base/banner/banner';
import { BaseButton } from '../base/button/button';
import { ComingSoon } from '../base/coming-soon/coming-soon';
import { mdiClose } from '@mdi/js';
import Icon from '@mdi/react';
import { Formik, FormikHelpers, FormikProps } from 'formik';
import { observer } from 'mobx-react';
import React, { useContext, useState } from 'react';
import {
    AddressFields,
    FacilityProfileFields,
    ProviderAutomationFields,
    ProviderProfileFields,
    RosterReadOnlyContext,
} from './roster-drawer-fields';
import * as S from './roster-drawer.styles';
import * as T from './roster-table.styles';

type Values = IRosterProviderValues | IRosterFacilityValues;

/**
 * The edit / create drawer (FE-109): the design's right-hand sheet for one provider or facility.
 *
 * What the design shows as "Unsaved changes" / "All changes saved" is formik's `dirty`. Revert
 * returns to what was loaded. Closing with unsaved changes asks first — in the footer, because a
 * MUI dialog would open underneath the drawer (see `roster-drawer.styles.ts`).
 */
export const RosterDrawer = observer(({ store }: { store: RosterStore }): React.ReactElement | null => {
    const drawer = store.drawer;
    if (!drawer) {
        return null;
    }

    const isProviders = drawer.tab === 'providers';
    const isNew = !drawer.recordId;
    const loading = drawer.status === 'loading';
    const missing = drawer.status === 'missing';
    const record: RosterRecord | undefined = drawer.record;

    return (
        <S.Panel className="fixed">
            {loading ? (
                <S.Centered>Loading…</S.Centered>
            ) : missing ? (
                <MissingRecord onClose={store.closeDrawer} />
            ) : isProviders ? (
                <DrawerForm
                    key={drawer.recordId ?? 'new-provider'}
                    store={store}
                    isNew={isNew}
                    title={isNew ? 'New provider' : rosterProviderName(record as RosterProvider)}
                    badge={isNew ? undefined : (record as RosterProvider | undefined)?.type}
                    subtitle="Update provider identifiers, contacts, and addresses."
                    initialValues={rosterProviderValues(record as RosterProvider | undefined)}
                    schema={rosterProviderSchema}
                    defaultAddressName={(values): string =>
                        rosterProviderName({
                            id: '',
                            ...(values as IRosterProviderValues),
                        }) || ''
                    }
                    renderFields={(): React.ReactElement => (
                        <>
                            <ProviderProfileFields />
                            {store.role === 'ops' && <ProviderAutomationFields />}
                        </>
                    )}
                    save={async (values): Promise<ISaved> => {
                        const saved = await store.saveProvider(
                            rosterProviderDraft(values as IRosterProviderValues, record as RosterProvider | undefined),
                        );
                        return { id: saved.id, name: rosterProviderName(saved) };
                    }}
                    noun="Provider"
                />
            ) : (
                <DrawerForm
                    key={drawer.recordId ?? 'new-facility'}
                    store={store}
                    isNew={isNew}
                    title={isNew ? 'New facility' : (record as RosterFacility).name}
                    subtitle="Manage facility profile details and addresses."
                    initialValues={rosterFacilityValues(record as RosterFacility | undefined)}
                    schema={rosterFacilitySchema}
                    defaultAddressName={(values): string => (values as IRosterFacilityValues).name}
                    renderFields={(): React.ReactElement => <FacilityProfileFields />}
                    save={async (values): Promise<ISaved> => {
                        const saved = await store.saveFacility(
                            rosterFacilityDraft(values as IRosterFacilityValues, record as RosterFacility | undefined),
                        );
                        return { id: saved.id, name: saved.name };
                    }}
                    noun="Facility"
                />
            )}
        </S.Panel>
    );
});

function MissingRecord({ onClose }: { onClose: () => void }): React.ReactElement {
    return (
        <S.Form as="div">
            <S.Header>
                <S.Title>Record not found</S.Title>
                <CloseButton onClick={onClose} />
            </S.Header>
            <S.Centered>It may have been disabled since the list was loaded.</S.Centered>
        </S.Form>
    );
}

interface IDrawerFormProps {
    store: RosterStore;
    isNew: boolean;
    title: string;
    badge?: string;
    subtitle: string;
    noun: 'Provider' | 'Facility';
    initialValues: Values;
    schema: typeof rosterProviderSchema | typeof rosterFacilitySchema;
    /** The name a new address starts with — the record's own name, as in the design. */
    defaultAddressName: (values: Values) => string;
    renderFields: () => React.ReactElement;
    save: (values: Values) => Promise<ISaved>;
}

interface ISaved {
    id: string;
    name: string;
}

function DrawerForm(props: IDrawerFormProps): React.ReactElement {
    const { store, isNew, title, badge, subtitle, noun, initialValues, schema, renderFields, save } = props;
    const toast = useContext(ToastContext);
    const [confirmingClose, setConfirmingClose] = useState(false);
    // No create / update route for this source (the provider app's API): the record is shown, not
    // edited, and Save waits disabled under a "Coming soon" hint (NS-4).
    const capabilities = store.dataSource.capabilities;
    const soon = rosterComingSoon(capabilities, isNew ? 'create' : 'update');
    const readOnly = !capabilities.write || soon;

    const handleSubmit = async (values: Values, helpers: FormikHelpers<Values>): Promise<void> => {
        helpers.setStatus(undefined);
        // The drawer this save belongs to. The table stays clickable while it runs, so the operator
        // can open another record meanwhile; a late result must not close that drawer or start a
        // claims flow for this record — it only reports how the save went.
        const openedAs = store.drawer;
        try {
            const saved = await save(values);
            if (store.drawer !== openedAs) {
                toast.notify?.(`${saved.name || noun} ${isNew ? 'created' : 'saved'}.`, 'right', 'light');
                return;
            }
            const tab = store.drawer?.tab ?? store.tab;
            store.closeDrawer();
            // An edit may need re-applying to the claims that already carry the old details; a new
            // record has no claims yet. The flow reports the outcome itself, including "unchanged".
            if (isNew || !store.startClaimsFlow({ tab, id: saved.id }, saved.name)) {
                toast.notify?.(isNew ? `${noun} created.` : 'Changes saved.', 'right', 'light');
            }
        } catch (error) {
            const message = error instanceof Error && error.message ? error.message : 'The save was refused.';
            if (store.drawer !== openedAs) {
                toast.notify?.(`${noun} not saved: ${message}`, 'right', 'warning');
                return;
            }
            // Kept in the drawer, with every field as typed — a failed save must not lose the edit.
            helpers.setStatus(message);
        }
    };

    return (
        <Formik initialValues={initialValues} validationSchema={schema} onSubmit={handleSubmit}>
            {(form: FormikProps<Values>): React.ReactElement => {
                // Not while saving: the save goes through either way, so closing or "Discard" would
                // only pretend to cancel it.
                const requestClose = (): void => {
                    if (form.isSubmitting) {
                        return;
                    }
                    if (form.dirty) {
                        setConfirmingClose(true);
                    } else {
                        store.closeDrawer();
                    }
                };
                return (
                    <S.Form
                        onSubmit={form.handleSubmit}
                        onKeyDown={(e: React.KeyboardEvent): void => {
                            if (e.key === 'Escape') {
                                e.stopPropagation();
                                requestClose();
                            }
                        }}
                        noValidate={true}
                    >
                        <S.Header>
                            <div style={{ minWidth: 0 }}>
                                <S.TitleRow>
                                    <S.Title title={title}>{title || 'Untitled'}</S.Title>
                                    {badge && <T.Chip tone="primary">{badge}</T.Chip>}
                                </S.TitleRow>
                                <S.Subtitle>{subtitle}</S.Subtitle>
                            </div>
                            <CloseButton onClick={requestClose} disabled={form.isSubmitting} />
                        </S.Header>
                        <RosterReadOnlyContext.Provider value={readOnly}>
                            <S.Body>
                                {form.status && (
                                    <Banner tone="danger" title="Not saved">
                                        {form.status}
                                    </Banner>
                                )}
                                {form.submitCount > 0 && !form.isValid && (
                                    <Banner tone="warning" title="Some fields need attention">
                                        Fix the highlighted fields, then save again.
                                    </Banner>
                                )}
                                {renderFields()}
                                <AddressFields defaultName={props.defaultAddressName(form.values)} />
                            </S.Body>
                        </RosterReadOnlyContext.Provider>
                        <S.Footer>
                            {readOnly ? (
                                <>
                                    <S.SaveState dirty={false}>
                                        Read only — this app cannot edit {noun === 'Provider' ? 'providers' : 'facilities'}
                                        {soon ? ' yet' : ''}.
                                    </S.SaveState>
                                    {soon && (
                                        <S.FooterActions>
                                            <ComingSoon hint={`saving ${noun === 'Provider' ? 'providers' : 'facilities'} from this account`}>
                                                <BaseButton type="button" variant="primary" disabled={true}>
                                                    {isNew ? `Create ${noun.toLowerCase()}` : 'Save changes'}
                                                </BaseButton>
                                            </ComingSoon>
                                        </S.FooterActions>
                                    )}
                                </>
                            ) : confirmingClose ? (
                                <>
                                    <S.SaveState dirty={true}>Discard unsaved changes?</S.SaveState>
                                    <S.FooterActions>
                                        <BaseButton
                                            type="button"
                                            variant="outlined"
                                            onClick={(): void => setConfirmingClose(false)}
                                        >
                                            Keep editing
                                        </BaseButton>
                                        <BaseButton
                                            type="button"
                                            variant="warning"
                                            disabled={form.isSubmitting}
                                            onClick={store.closeDrawer}
                                        >
                                            Discard
                                        </BaseButton>
                                    </S.FooterActions>
                                </>
                            ) : (
                                <>
                                    <S.SaveState dirty={form.dirty}>
                                        {form.dirty
                                            ? 'Unsaved changes'
                                            : isNew
                                              ? 'Nothing entered yet'
                                              : 'All changes saved'}
                                    </S.SaveState>
                                    <S.FooterActions>
                                        <BaseButton
                                            type="button"
                                            variant="outlined"
                                            disabled={!form.dirty || form.isSubmitting}
                                            onClick={(): void => {
                                                form.resetForm();
                                                form.setStatus(undefined);
                                            }}
                                        >
                                            Revert
                                        </BaseButton>
                                        <BaseButton
                                            type="submit"
                                            variant="primary"
                                            disabled={form.isSubmitting || (!isNew && !form.dirty)}
                                        >
                                            {form.isSubmitting
                                                ? 'Saving…'
                                                : isNew
                                                  ? `Create ${noun.toLowerCase()}`
                                                  : 'Save changes'}
                                        </BaseButton>
                                    </S.FooterActions>
                                </>
                            )}
                        </S.Footer>
                    </S.Form>
                );
            }}
        </Formik>
    );
}

function CloseButton({ onClick, disabled }: { onClick: () => void; disabled?: boolean }): React.ReactElement {
    return (
        <BaseButton type="button" variant="unstyled" aria-label="Close" onClick={onClick} disabled={disabled}>
            <Icon path={mdiClose} size={0.9} />
        </BaseButton>
    );
}
